// src/components/ChartComponent.js
import React, { useEffect, useRef, memo } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';

const ChartComponent = memo(({ chartData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartData) return;
    
    const ctx = chartRef.current.getContext('2d');
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Parse the configuration based on input type
    let chartConfig;
    if (typeof chartData === 'string') {
      chartConfig = JSON.parse(chartData);
    } else if (chartData.chart_js_json) {
      chartConfig = typeof chartData.chart_js_json === 'string' 
        ? JSON.parse(chartData.chart_js_json)
        : chartData.chart_js_json;
    } else {
      chartConfig = chartData;
    }

    // Force responsive behavior with proper settings for hover detection
    chartConfig.options = {
      responsive: true,
      maintainAspectRatio: false, // Changed to false to prevent sizing conflicts
      interaction: {
        intersect: false,
        mode: 'index'
      },
      ...chartConfig.options,
    };

    // Convert string callbacks to functions using a safer approach
    if (chartConfig.options?.scales?.y?.ticks?.callback && 
        typeof chartConfig.options.scales.y.ticks.callback === 'string') {
      
      // Only apply dollar formatting for monetary charts
      const chartTitle = chartConfig.options?.plugins?.title?.text || '';
      const yAxisTitle = chartConfig.options?.scales?.y?.title?.text || '';
      
      // Check if this is a monetary chart (contains price, income, value, etc.)
      const isMonetaryChart = /price|income|value|mortgage|rent|buying.power|cost|payment|\$/.test(
        (chartTitle + ' ' + yAxisTitle).toLowerCase()
      );
      
      // Check if this is a count/household chart
      const isCountChart = /count|household|buyer|number|motivated/.test(
        (chartTitle + ' ' + yAxisTitle).toLowerCase()
      );
      
      if (isMonetaryChart && !isCountChart) {
        // Apply dollar formatting for monetary charts
      chartConfig.options.scales.y.ticks.callback = (value) => {
        return "$" + value.toLocaleString();
      };
      } else {
        // Apply number formatting for count charts
        chartConfig.options.scales.y.ticks.callback = (value) => {
          return value.toLocaleString();
        };
      }
    }

    // Convert tooltip callback string to function
    if (chartConfig.options?.plugins?.tooltip?.callbacks?.label && 
        typeof chartConfig.options.plugins.tooltip.callbacks.label === 'string') {
      
      // Check if this is a monetary chart - be more specific to avoid false positives
      const chartTitle = chartConfig.options?.plugins?.title?.text || '';
      const yAxisTitle = chartConfig.options?.scales?.y?.title?.text || '';
      const isMonetaryChart = /\bprice\b|\bincome\b|\bmortgage\b|\brent\b|\bcost\b|\bpayment\b|\$|buying.power/.test(
        (chartTitle + ' ' + yAxisTitle).toLowerCase()
      );
      
      chartConfig.options.plugins.tooltip.callbacks.label = function(context) {
        const label = context.dataset.label || '';
        const value = context.parsed.y;

        if (chartConfig.options?.plugins?.title?.text?.includes('Over Time')) {
          // Get the x-axis label (year or date string) from the chart labels
          const xLabel = context.chart.data.labels?.[context.dataIndex] || context.label || '';
          if (isMonetaryChart) {
            return `${label}: $${value.toLocaleString()} (${xLabel})`;
          } else {
            return `${label}: ${value.toLocaleString()} (${xLabel})`;
          }
        }
        
        // Apply conditional formatting based on chart type
        if (isMonetaryChart) {
          return `${label}: $${value.toLocaleString()}`;
        } else {
          return `${label}: ${value.toLocaleString()}`;
        }
      };
    }

    if (chartConfig.options?.scales?.y?.ticks?.stepSize && 
        typeof chartConfig.options.scales.y.ticks.stepSize === 'string') {
      // Create a safe stepSize function that returns a fixed step size
      chartConfig.options.scales.y.ticks.stepSize = (context) => {
        // You might want to add specific step size logic here
        return 1;
      };
    }

    // Get the container element to determine appropriate sizing
    const container = chartRef.current?.parentElement;
    if (!container) {
      console.warn('Chart container not found - skipping chart creation');
      return;
    }
    const containerWidth = container.offsetWidth || 400;
    const containerHeight = container.offsetHeight || 300;

    // Set canvas size to match container before creating chart
    chartRef.current.width = containerWidth;
    chartRef.current.height = containerHeight;
    chartRef.current.style.width = containerWidth + 'px';
    chartRef.current.style.height = containerHeight + 'px';

    chartInstance.current = new Chart(ctx, chartConfig);

    // Force a resize after chart creation to ensure proper coordinate mapping
    setTimeout(() => {
      // Use isConnected to verify the canvas is still in the DOM
      if (chartInstance.current && chartRef.current?.isConnected) {
        try {
          chartInstance.current.resize();
        } catch (error) {
          // Silently ignore - DOM was likely removed during section switch
        }
      }
    }, 100);

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <canvas 
        ref={chartRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          display: 'block'
        }}
      />
    </div>
  );
});

export default ChartComponent;
